import { useEffect, useState } from "react";

/**
 * Three-way theme choice. "auto" is the default and the only one that looks at
 * the OS; the other two override it. The resolved result is stamped on <html>
 * as data-theme, which is what every colour token and `dark:` utility keys off
 * -- see the comment on the dark block in index.css.
 *
 * The same value is read by the inline boot script in index.html before React
 * mounts, so the first paint is already the right theme. Keep the storage key
 * in step with that script.
 */
export type ThemePreference = "light" | "dark" | "auto";

export const THEME_STORAGE_KEY = "intern-seek:theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** localStorage throws in private mode and comes back empty with site data blocked. */
function readStored(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "auto") return raw;
  } catch {
    // Ignored: an unreadable preference is just the default.
  }
  return "auto";
}

function prefersDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

function stamp(preference: ThemePreference): void {
  const dark = preference === "dark" || (preference === "auto" && prefersDark());
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readStored);

  useEffect(() => {
    stamp(preference);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // Ignored: the choice still applies for this page view.
    }

    // Only "auto" tracks the OS, and it has to keep tracking it -- someone on a
    // schedule-based dark mode would otherwise stay on whatever was true when
    // the page loaded.
    if (preference !== "auto") return;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => stamp("auto");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [preference]);

  return { preference, setPreference };
}
